import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Box, 
  Button, 
  Stack, 
  Typography, 
  Alert,
  Chip,
  Paper
} from '@mui/material';
import { 
  Save as SaveIcon, 
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon 
} from '@mui/icons-material';

interface Props {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave?: () => Promise<void>;
  hasChanges?: boolean;
  loading?: boolean;
  lastSaved?: Date | null;
}

const MemoTab = ({ 
  value, 
  onChange, 
  onSave, 
  hasChanges = false, 
  loading = false,
  lastSaved 
}: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 📝 저장 핸들러
  const handleSave = async () => {
    if (!onSave || !hasChanges) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await onSave();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Memo save error:', error);
      setSaveError('메모 저장 중 오류가 발생했습니다.');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  // 📅 마지막 저장 시간 포매팅
  const formatLastSaved = (date: Date | null | undefined) => {
    if (!date) return '저장된 적 없음';
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return '방금 전';
    if (minutes < 60) return `${minutes}분 전`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}시간 전`;
    
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 3, 
        border: '1px solid #e1e8ed', 
        bgcolor: '#fff'
      }}
    >
      {/* 🏷️ 헤더 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          📝 관리자 메모
        </Typography>
        
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* 📊 상태 표시 */}
          {hasChanges ? (
            <Chip
              icon={<ScheduleIcon />}
              label="변경사항 있음"
              size="small"
              color="warning"
              sx={{ fontWeight: 600 }}
            />
          ) : (
            <Chip
              icon={<CheckIcon />}
              label="저장됨"
              size="small"
              color="success"
              sx={{ fontWeight: 600 }}
            />
          )}

          {/* ⏰ 마지막 저장 시간 */}
          <Typography variant="caption" color="text.secondary">
            {formatLastSaved(lastSaved)}
          </Typography>
        </Stack>
      </Stack>

      {/* 📝 텍스트 에리어 */}
      <TextField
        fullWidth
        multiline
        rows={4} // 8에서 4로 줄임
        value={value}
        onChange={onChange}
        placeholder="신청서에 대한 관리자 메모를 입력하세요&#13;&#10;• 검수 과정에서 발견된 사항&#13;&#10;• 고객과의 통화 내용&#13;&#10;• 특이사항 등을 기록해주세요"
        sx={{
          mb: 2, // 3에서 2로 줄임
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            bgcolor: '#f8fafc',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            '&:hover': {
              bgcolor: '#f1f5f9',
            },
            '&.Mui-focused': {
              bgcolor: '#fff',
              boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
            },
          },
          '& .MuiInputBase-input': {
            minHeight: '80px !important', // 160px에서 80px로 줄임
          },
        }}
      />

      {/* 💾 저장 버튼 및 상태 메시지 */}
      <Stack spacing={1.5}>
        {/* 저장 버튼 영역을 더 간결하게 */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '0.8rem' }}
          >
            💡 변경사항은 저장 버튼을 클릭해야 반영됩니다
          </Typography>

          <Button
            variant="contained"
            startIcon={isSaving ? <ScheduleIcon /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving || loading}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              minWidth: { xs: '100%', sm: 140 }, // 모바일에서 전체 너비
              background: hasChanges 
                ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
                : 'grey.300',
              boxShadow: hasChanges 
                ? '0 4px 20px rgba(25, 118, 210, 0.3)'
                : 'none',
              '&:hover': hasChanges ? {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                boxShadow: '0 6px 28px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-1px)',
              } : {},
              '&:disabled': {
                background: 'grey.300',
                color: 'grey.500',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {isSaving ? '저장 중...' : '메모 저장'}
          </Button>
        </Stack>

        {/* ✅ 성공 메시지 */}
        {saveSuccess && (
          <Alert 
            severity="success" 
            sx={{ 
              borderRadius: 2,
              py: 0.5, // 높이를 줄여서 더 간결하게
            }}
            onClose={() => setSaveSuccess(false)}
          >
            <Typography variant="body2" fontWeight={600}>
              메모가 성공적으로 저장되었습니다.
            </Typography>
          </Alert>
        )}

        {/* ❌ 에러 메시지 */}
        {saveError && (
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: 2,
              py: 0.5, // 높이를 줄여서 더 간결하게
            }}
            onClose={() => setSaveError(null)}
          >
            <Typography variant="body2" fontWeight={600}>
              {saveError}
            </Typography>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default MemoTab; 